"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { BulkEmailAttachmentPayload, CreateBulkEmailPayload, LexicalText, RoleMapping } from "@/schema"
import { useVoiceTranscriptionStore } from "@/stores/use-voice-transcription-store"
import { customFieldsToPlaceholders } from "@/utils/custom-fields-to-placeholders"
import { ScheduledSendButton } from "@ui/scheduled-send/scheduled-send-button"
import {
  Button,
  DialogHeader,
  DialogTitle,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Textarea,
  TooltipProvider,
} from "@ui/shad"
import { ArrowLeft, ArrowRight, Braces, Loader2, Mail, Mic, Paperclip, Settings2, Sparkles, X } from "lucide-react"
import { toast } from "sonner"

import { useCreateBulkEmail } from "@/hooks/api/bulk_emails"
import { useFetchCustomFields } from "@/hooks/api/custom_fields/queries"
import { useEmailSendingIntegration } from "@/hooks/api/email_integrations/use-email-sending-integration"
import { useReviseEmail } from "@/hooks/api/email_messages"
import { useFetchEmailRecipients } from "@/hooks/api/email_recipients"
import { buildSendableAddresses, getDefaultAddress, SendableAddress } from "@/hooks/use-email-compose-logic"
import { EmailRecipientsField } from "@/components/automations/edit-panel/actions/email-recipients-field"
import type { EmailRecipientsConfig } from "@/components/automations/types"
import { CustomDialogContent } from "@/components/dialogs/custom-dialog-content"
import EmailBodyEditor from "@/components/email/body-editor"
import { SEQUENCE_RECIPIENT_PLACEHOLDERS } from "@/components/email/email-placeholders"
import { EmailSubjectField } from "@/components/email/email-subject-field"
import { EmailTemplatePicker } from "@/components/email/email-template-picker"
import { FromEmailSelector } from "@/components/email/from-email-selector"
import {
  getSignatureForEmail,
  removeTrailingSignature,
  signatureHtmlFromContent,
} from "@/components/email/signature-utils"
import { EditorRefHandle } from "@/components/lexical/editors/email"
import { INSERT_PLACEHOLDER_COMMAND, PlaceholderPickerPopover } from "@/components/lexical/plugins/placeholder"
import { EMAIL_TEMPLATE_PLACEHOLDERS } from "@/components/settings/email-templates/email-template-placeholders"

import { BulkEmailRecipientData } from "./recipient-item"
import { RecipientList } from "./recipient-list"
import { ReviewEmailPreview } from "./review-preview"
import { RecipientIdentifier } from "./use-bulk-email-compose"

const MAX_ATTACHMENTS = 3
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB per file
const MAX_TOTAL_ATTACHMENT_SIZE = 25 * 1024 * 1024 // 25MB total

interface PendingAttachment {
  file: File
  id: string
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const DEFAULT_ROLE_MAPPING: EmailRecipientsConfig = {
  head_of_household: "to",
  spouse_or_partner: "to",
}

interface BulkEmailComposeDialogProps {
  recipientIds: RecipientIdentifier[]
  onClose: () => void
  onSent?: () => void
}

type Step = "compose" | "review"

export function BulkEmailComposeDialog({ recipientIds, onClose, onSent }: BulkEmailComposeDialogProps) {
  const recipientParams = useMemo(() => {
    const params: { household_ids?: string[]; contact_ids?: string[]; person_ids?: string[] } = {}
    const householdIds = recipientIds.filter((r) => r.type === "household").map((r) => r.id)
    const contactIds = recipientIds.filter((r) => r.type === "contact").map((r) => r.id)
    const personIds = recipientIds.filter((r) => r.type === "person").map((r) => r.id)
    if (householdIds.length > 0) params.household_ids = householdIds
    if (contactIds.length > 0) params.contact_ids = contactIds
    if (personIds.length > 0) params.person_ids = personIds
    return params
  }, [recipientIds])

  const { data: recipientsData, isLoading: isLoadingRecipients } = useFetchEmailRecipients(recipientParams)
  const recipients = useMemo(() => recipientsData?.recipients ?? [], [recipientsData])
  const recipientsWithEmail = useMemo(() => recipients.filter((r) => r.email), [recipients])
  const [step, setStep] = useState<Step>("compose")
  const [selectedAddress, setSelectedAddress] = useState<SendableAddress | null>(null)
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState<LexicalText>({ html: "" })
  const [selectedRecipient, setSelectedRecipient] = useState<BulkEmailRecipientData | null>(null)
  const [fromPopoverOpen, setFromPopoverOpen] = useState(false)
  const [roleMapping, setRoleMapping] = useState<EmailRecipientsConfig>(DEFAULT_ROLE_MAPPING)
  const [recipientsPopoverOpen, setRecipientsPopoverOpen] = useState(false)
  const [revisionPopoverOpen, setRevisionPopoverOpen] = useState(false)
  const [revisionPrompt, setRevisionPrompt] = useState("")
  const [sending, setSending] = useState(false)
  const [scheduling, setScheduling] = useState(false)
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const previousAddressRef = useRef<SendableAddress | null>(null)
  const editorRef = useRef<EditorRefHandle>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const createBulkEmail = useCreateBulkEmail()
  const reviseEmailMutation = useReviseEmail()
  const isRevising = reviseEmailMutation.isPending
  const { sendingIntegrations, emailIntegrations } = useEmailSendingIntegration()
  const {
    trigger: triggerVoiceTranscription,
    isActive: isVoiceActive,
    stop: stopVoiceTranscription,
  } = useVoiceTranscriptionStore()

  const allSendableAddresses = useMemo(() => buildSendableAddresses(sendingIntegrations), [sendingIntegrations])

  const { data: customFieldsData } = useFetchCustomFields("Household")
  const placeholders = useMemo(() => {
    const onlyHouseholds =
      recipientsWithEmail.length > 0 && recipientsWithEmail.every((recipient) => recipient.type === "household")
    const emailPlaceholders = onlyHouseholds
      ? EMAIL_TEMPLATE_PLACEHOLDERS
      : EMAIL_TEMPLATE_PLACEHOLDERS.filter((placeholder) => !placeholder.value.includes("household."))
    return [
      ...SEQUENCE_RECIPIENT_PLACEHOLDERS,
      ...emailPlaceholders,
      ...(onlyHouseholds ? customFieldsToPlaceholders(customFieldsData?.custom_fields ?? []) : []),
    ]
  }, [customFieldsData, recipientsWithEmail])

  const getIntegrationById = useCallback(
    (integrationId: string) => emailIntegrations?.find((i) => i.id === integrationId),
    [emailIntegrations]
  )
  const selectedIntegration = useMemo(
    () => (selectedAddress ? getIntegrationById(selectedAddress.integrationId) : sendingIntegrations[0]),
    [getIntegrationById, selectedAddress, sendingIntegrations]
  )

  const getSignatureHtml = useCallback(
    (integrationId: string, fromEmail: string) => {
      const integration = getIntegrationById(integrationId)
      return signatureHtmlFromContent(getSignatureForEmail(integration, fromEmail)?.content)
    },
    [getIntegrationById]
  )

  useEffect(() => {
    if (selectedAddress || !sendingIntegrations?.length) return

    const defaultAddr = getDefaultAddress(sendingIntegrations)
    if (defaultAddr) {
      setSelectedAddress(defaultAddr)
      previousAddressRef.current = defaultAddr

      const signatureHtml = getSignatureHtml(defaultAddr.integrationId, defaultAddr.email)
      if (signatureHtml) {
        setBody({ html: `<p></p><p></p>${signatureHtml}` })
      }
    }
  }, [selectedAddress, sendingIntegrations, getSignatureHtml])

  const handleAddressChange = useCallback(
    (newAddress: SendableAddress) => {
      const previousAddress = previousAddressRef.current

      if (
        previousAddress &&
        (previousAddress.integrationId !== newAddress.integrationId || previousAddress.email !== newAddress.email)
      ) {
        const oldSignatureHtml = getSignatureHtml(previousAddress.integrationId, previousAddress.email)
        const newSignatureHtml = getSignatureHtml(newAddress.integrationId, newAddress.email)

        const currentHtml = body.html || ""
        const { bodyHtml } = oldSignatureHtml
          ? removeTrailingSignature(currentHtml, oldSignatureHtml)
          : { bodyHtml: currentHtml }

        const newHtml = newSignatureHtml ? `${bodyHtml}${newSignatureHtml}` : bodyHtml
        const newContent = { html: newHtml }
        setBody(newContent)
        editorRef.current?.updateContent(newContent)
      }

      previousAddressRef.current = newAddress
      setSelectedAddress(newAddress)
      setFromPopoverOpen(false)
    },
    [getSignatureHtml, body.html]
  )

  const hasHouseholdRecipients = useMemo(() => recipients.some((r) => r.type === "household"), [recipients])

  const roleDescription = useMemo(() => {
    if (!hasHouseholdRecipients) {
      return "Sending individual emails"
    }

    const hoh = roleMapping.head_of_household
    const spouse = roleMapping.spouse_or_partner

    const toRoles: string[] = []
    const ccRoles: string[] = []
    const bccRoles: string[] = []

    if (hoh === "to") toRoles.push("head of household")
    if (hoh === "cc") ccRoles.push("head of household")
    if (hoh === "bcc") bccRoles.push("head of household")

    if (spouse === "to") toRoles.push("spouse / partner")
    if (spouse === "cc") ccRoles.push("spouse / partner")
    if (spouse === "bcc") bccRoles.push("spouse / partner")

    if (toRoles.length === 0 && ccRoles.length === 0 && bccRoles.length === 0) {
      return "No recipients selected"
    }

    let description = ""

    if (toRoles.length > 0) {
      description = `Emailing ${toRoles.join(" and ")}`
      if (ccRoles.length > 0) {
        description += `, CC'ing ${ccRoles.join(" and ")}`
      }
      if (bccRoles.length > 0) {
        description += `, BCC'ing ${bccRoles.join(" and ")}`
      }
    } else if (ccRoles.length > 0 && bccRoles.length > 0) {
      description = `CC'ing ${ccRoles.join(" and ")}, BCC'ing ${bccRoles.join(" and ")}`
    } else if (ccRoles.length > 0) {
      description = `CC'ing ${ccRoles.join(" and ")}`
    } else {
      description = `BCC'ing ${bccRoles.join(" and ")}`
    }

    return description
  }, [hasHouseholdRecipients, roleMapping])

  const handleBodyChange = useCallback((lexicalText: LexicalText) => {
    setBody(lexicalText)
  }, [])

  const handleApplyTemplate = useCallback(
    (templateSubject: string, templateBody: LexicalText) => {
      setSubject(templateSubject)

      const signatureHtml = selectedAddress
        ? getSignatureHtml(selectedAddress.integrationId, selectedAddress.email)
        : ""
      const bodyHtml = templateBody.html || ""
      const finalContent = { html: signatureHtml ? `${bodyHtml}${signatureHtml}` : bodyHtml }
      setBody(finalContent)
      editorRef.current?.updateContent(finalContent)
    },
    [selectedAddress, getSignatureHtml]
  )

  const handleRevisionPopoverChange = useCallback(
    (open: boolean) => {
      if (open) {
        if (!body.html?.trim()) {
          toast.error("Add content to your email before asking for revisions")
          return
        }
        reviseEmailMutation.reset()
        setRevisionPopoverOpen(true)
        return
      }

      if (isRevising) return
      setRevisionPopoverOpen(false)
      reviseEmailMutation.reset()
    },
    [body.html, isRevising, reviseEmailMutation]
  )

  const runRevision = useCallback(
    (instructions: string) => {
      const trimmed = instructions.trim()
      if (!trimmed) {
        toast.error("Describe how you'd like the email to change")
        return
      }

      const htmlBody = body.html?.trim()
      if (!htmlBody) {
        toast.error("We couldn't read the email body to revise it")
        return
      }

      reviseEmailMutation.reset()

      const integrationSignatureHtml = selectedAddress
        ? getSignatureHtml(selectedAddress.integrationId, selectedAddress.email)
        : ""
      const bodyWithoutSignature = integrationSignatureHtml
        ? removeTrailingSignature(htmlBody, integrationSignatureHtml).bodyHtml
        : htmlBody

      setRevisionPopoverOpen(false)

      reviseEmailMutation.mutate(
        {
          current_body: bodyWithoutSignature,
          revision_prompt: trimmed,
          subject: subject,
        },
        {
          onSuccess: (data) => {
            const revisedBody = data.revised_body || ""
            const finalHtml = integrationSignatureHtml ? `${revisedBody}${integrationSignatureHtml}` : revisedBody
            setBody({ html: finalHtml })

            if (typeof data.subject === "string") {
              setSubject(data.subject)
            }

            toast.success("Email updated")
            setRevisionPrompt("")
          },
          onError: () => {
            setRevisionPrompt(trimmed)
            setRevisionPopoverOpen(true)
            reviseEmailMutation.reset()
          },
        }
      )
    },
    [body.html, subject, selectedAddress, getSignatureHtml, reviseEmailMutation]
  )

  const handleApplyRevision = useCallback(() => runRevision(revisionPrompt), [runRevision, revisionPrompt])

  const handleSelectPlaceholder = useCallback(
    (placeholder: { id: string; label: string; value: string; category: string }) => {
      const editor = editorRef.current?.getEditor()
      if (editor) {
        editor.focus()
        editor.dispatchCommand(INSERT_PLACEHOLDER_COMMAND, placeholder)
      }
    },
    []
  )

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (!files) return

      let count = attachments.length
      let totalSize = attachments.reduce((sum, a) => sum + a.file.size, 0)
      const toAdd: PendingAttachment[] = []

      for (const file of Array.from(files)) {
        if (count >= MAX_ATTACHMENTS) {
          toast.error(`Maximum ${MAX_ATTACHMENTS} attachments allowed`)
          break
        }
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`${file.name} is too large. Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}`)
          continue
        }
        if (totalSize + file.size > MAX_TOTAL_ATTACHMENT_SIZE) {
          toast.error(`Total attachment size cannot exceed ${formatFileSize(MAX_TOTAL_ATTACHMENT_SIZE)}`)
          break
        }
        toAdd.push({ file, id: `${Date.now()}-${file.name}` })
        count++
        totalSize += file.size
      }

      if (toAdd.length > 0) setAttachments((prev) => [...prev, ...toAdd])
      if (fileInputRef.current) fileInputRef.current.value = ""
    },
    [attachments]
  )

  const handleRemoveAttachment = useCallback((attachmentId: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
  }, [])

  const processAttachments = useCallback(async (): Promise<BulkEmailAttachmentPayload[]> => {
    return Promise.all(
      attachments.map(
        (attachment) =>
          new Promise<BulkEmailAttachmentPayload>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
              const base64Data = (reader.result as string).split(",")[1]
              resolve({
                filename: attachment.file.name,
                content_type: attachment.file.type,
                size: attachment.file.size,
                file: base64Data,
              })
            }
            reader.onerror = reject
            reader.readAsDataURL(attachment.file)
          })
      )
    )
  }, [attachments])

  const buildBulkEmailPayload = useCallback(
    (scheduledStartAt?: string): CreateBulkEmailPayload | null => {
      if (!selectedAddress) {
        toast.error("Please select an email integration")
        return null
      }

      if (!subject.trim()) {
        toast.error("Please enter a subject")
        return null
      }

      if (recipientsWithEmail.length === 0) {
        toast.error("No recipients with valid email addresses")
        return null
      }

      const roleMappingForApi: RoleMapping | undefined = hasHouseholdRecipients
        ? (Object.fromEntries(Object.entries(roleMapping).filter(([, value]) => value !== "none")) as RoleMapping)
        : undefined

      const payload: CreateBulkEmailPayload = {
        subject,
        body: { html: body.html || "" },
        email_integration_id: selectedAddress.integrationId,
        from_email: selectedAddress.email,
        role_mapping: roleMappingForApi,
        bulk_email_recipients_attributes: recipientsWithEmail.map((r) => {
          if (r.type === "household") {
            return { household_id: r.id }
          } else if (r.type === "contact") {
            return { contact_id: r.id }
          } else {
            return { person_id: r.id }
          }
        }),
      }

      return scheduledStartAt ? { ...payload, scheduled_start_at: scheduledStartAt } : payload
    },
    [selectedAddress, subject, recipientsWithEmail, hasHouseholdRecipients, roleMapping, body.html]
  )

  const handleSend = useCallback(async () => {
    const payload = buildBulkEmailPayload()
    if (!payload) return

    setSending(true)

    try {
      const emailAttachments = await processAttachments()
      const finalPayload = emailAttachments.length > 0 ? { ...payload, email_attachments: emailAttachments } : payload
      await createBulkEmail.mutateAsync(finalPayload)
      toast.success(
        `Sending emails to ${recipientsWithEmail.length} recipient${recipientsWithEmail.length === 1 ? "" : "s"}`
      )
      onSent?.()
      onClose()
    } finally {
      setSending(false)
    }
  }, [buildBulkEmailPayload, processAttachments, createBulkEmail, recipientsWithEmail.length, onSent, onClose])

  const handleSchedule = useCallback(
    async (scheduledFor: string) => {
      const payload = buildBulkEmailPayload(scheduledFor)
      if (!payload) return

      setScheduling(true)

      try {
        const emailAttachments = await processAttachments()
        const finalPayload = emailAttachments.length > 0 ? { ...payload, email_attachments: emailAttachments } : payload
        await createBulkEmail.mutateAsync(finalPayload)
        toast.success(
          `Scheduled emails to ${recipientsWithEmail.length} recipient${recipientsWithEmail.length === 1 ? "" : "s"}`
        )
        onSent?.()
        onClose()
      } finally {
        setScheduling(false)
      }
    },
    [buildBulkEmailPayload, processAttachments, createBulkEmail, recipientsWithEmail.length, onSent, onClose]
  )

  const hasContent = !!(subject.trim() || body.html?.trim())

  const handleReview = useCallback(() => {
    if (!selectedAddress) {
      toast.error("Please select an email integration")
      return
    }

    if (!subject.trim()) {
      toast.error("Please enter a subject")
      return
    }

    if (recipientsWithEmail.length === 0) {
      toast.error("No recipients with valid email addresses")
      return
    }

    setSelectedRecipient(recipientsWithEmail[0])
    setStep("review")
  }, [selectedAddress, subject, recipientsWithEmail])

  const handleBackToCompose = useCallback(() => {
    setSelectedRecipient(null)
    setStep("compose")
  }, [])

  if (isLoadingRecipients) {
    return (
      <CustomDialogContent title="Bulk email">
        <div className="flex h-full items-center justify-center">
          <Loader2 className="text-muted-foreground size-8 animate-spin" />
        </div>
      </CustomDialogContent>
    )
  }

  return (
    <CustomDialogContent title="Bulk email" titleProvided>
      <TooltipProvider>
        <div className="flex h-full flex-col">
          <DialogHeader>
            <DialogTitle>{step === "compose" ? "Compose email" : "Review emails"}</DialogTitle>
          </DialogHeader>

          <div className="flex h-[70vh] max-h-[760px] min-h-0 xl:h-[60vh]">
            <div className="flex w-[60%] flex-col">
              {step === "compose" ? (
                <>
                  <FromEmailSelector
                    selectedAddress={selectedAddress}
                    allAddresses={allSendableAddresses}
                    isOpen={fromPopoverOpen}
                    onOpenChange={setFromPopoverOpen}
                    onSelect={handleAddressChange}
                    className="border-b px-4 py-2"
                  />

                  <div className="flex items-center justify-between border-b px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-tertiary-foreground w-10 flex-none text-sm font-medium">To</span>
                      <div className="bg-secondary text-foreground flex items-center gap-1.5 rounded-full px-3 py-1 text-sm">
                        <Mail className="size-3.5" />
                        <span>{roleDescription}</span>
                      </div>
                    </div>
                    {hasHouseholdRecipients && (
                      <Popover open={recipientsPopoverOpen} onOpenChange={setRecipientsPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="xs">
                            <Settings2 className="size-3.5" />
                            Change roles
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-72 pt-2">
                          <EmailRecipientsField
                            value={roleMapping}
                            onChange={setRoleMapping}
                            label="Map household roles in the email"
                            includePerson={false}
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>

                  <EmailSubjectField
                    value={subject}
                    onChange={setSubject}
                    placeholder="Add a subject"
                    enablePlaceholders
                    placeholders={placeholders}
                    className="border-b px-4 py-2"
                  />

                  <div className="relative min-h-[300px] grow">
                    {isRevising && (
                      <div className="bg-background border-border/50 absolute inset-0 z-10 flex items-center justify-center border shadow-sm">
                        <Loader2 className="text-muted-foreground size-5 animate-spin" />
                      </div>
                    )}
                    <ScrollArea className="h-full">
                      <EmailBodyEditor
                        ref={editorRef}
                        onChange={handleBodyChange}
                        initialContent={body}
                        provider={selectedIntegration?.provider}
                        placeholder="Start typing your email, or create a template"
                        enablePlaceholders
                        placeholders={placeholders}
                        styleOverrides={{ "--lexical-editor-min-height": "280px" }}
                      />
                    </ScrollArea>
                  </div>
                  {attachments.length > 0 && (
                    <div className="border-t px-4 py-2">
                      <div className="flex flex-wrap gap-2">
                        {attachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="bg-secondary text-secondary-foreground flex items-center gap-2 rounded-md px-3 py-1.5 text-sm"
                          >
                            <Paperclip className="size-3.5" />
                            <span className="max-w-[200px] truncate">{attachment.file.name}</span>
                            <span className="text-muted-foreground text-xs">
                              ({formatFileSize(attachment.file.size)})
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              className="hover:text-destructive ml-1"
                            >
                              <X className="size-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                selectedRecipient &&
                selectedAddress && (
                  <ReviewEmailPreview
                    recipient={selectedRecipient}
                    emailIntegrationId={selectedAddress.integrationId}
                    subject={subject}
                    body={body}
                    provider={selectedIntegration?.provider}
                    roleMapping={roleMapping}
                  />
                )
              )}
            </div>

            <div className="w-[40%] border-l">
              <RecipientList
                recipients={recipients}
                selectedRecipientId={step === "review" ? selectedRecipient?.id : undefined}
                onSelectRecipient={step === "review" ? setSelectedRecipient : undefined}
              />
            </div>
          </div>

          <div className="border-t px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex min-w-0 items-center gap-1">
                {step === "compose" ? (
                  <>
                    <Popover open={revisionPopoverOpen} onOpenChange={handleRevisionPopoverChange}>
                      <PopoverTrigger asChild>
                        <Button size="iconSm" variant="ghost" disabled={isRevising} tooltip="Rewrite with AI">
                          {isRevising ? (
                            <Loader2 className="text-icon size-4 animate-spin" />
                          ) : (
                            <Sparkles className="text-icon size-4" />
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" side="top" sideOffset={8} className="w-96 space-y-2 px-3 py-2">
                        <div className="space-y-2">
                          <Label htmlFor="revision-prompt">Describe the changes you want</Label>
                          <Textarea
                            id="revision-prompt"
                            value={revisionPrompt}
                            onChange={(event) => setRevisionPrompt(event.target.value)}
                            placeholder="e.g. Make this warmer and mention follow up on our last meeting."
                            rows={4}
                            className="px-2 py-1"
                            disabled={isRevising}
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              handleRevisionPopoverChange(false)
                              setRevisionPrompt("")
                            }}
                            disabled={isRevising}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleApplyRevision}
                            disabled={isRevising || !revisionPrompt.trim()}
                          >
                            {isRevising ? (
                              <>
                                <Loader2 className="size-4 animate-spin" />
                                Applying...
                              </>
                            ) : (
                              "Rewrite email"
                            )}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <PlaceholderPickerPopover placeholders={placeholders} onSelect={handleSelectPlaceholder}>
                      <Button size="iconSm" variant="ghost" tooltip="Insert variable">
                        <Braces className="text-icon size-4" />
                      </Button>
                    </PlaceholderPickerPopover>
                    <EmailTemplatePicker hasContent={hasContent} onApply={handleApplyTemplate} skipRender />
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                      accept="*/*"
                    />
                    <Button
                      size="iconSm"
                      variant="ghost"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={attachments.length >= MAX_ATTACHMENTS}
                      tooltip="Attach file"
                    >
                      <Paperclip className="text-icon" />
                    </Button>
                    <Button
                      size="iconSm"
                      variant={isVoiceActive ? "secondary" : "ghost"}
                      onClick={() => {
                        if (isVoiceActive) {
                          stopVoiceTranscription()
                        } else {
                          editorRef.current?.focus()
                          setTimeout(() => triggerVoiceTranscription(), 50)
                        }
                      }}
                      tooltip={isVoiceActive ? "Stop dictation" : "Dictate"}
                    >
                      <Mic className={isVoiceActive ? "text-red-accent animate-pulse" : "text-icon"} />
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" size="sm" onClick={handleBackToCompose}>
                    <ArrowLeft className="size-3.5" />
                    Back to compose
                  </Button>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {step === "compose" ? (
                  <Button onClick={handleReview} disabled={recipientsWithEmail.length === 0 || isRevising}>
                    <ArrowRight className="size-4" />
                    Review emails ({recipientsWithEmail.length})
                  </Button>
                ) : (
                  <ScheduledSendButton
                    onSend={handleSend}
                    onSchedule={handleSchedule}
                    sendLabel={`Send emails (${recipientsWithEmail.length})`}
                    sending={sending}
                    scheduling={scheduling}
                    disabled={recipientsWithEmail.length === 0 || !selectedAddress || isRevising}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    </CustomDialogContent>
  )
}
